<template>
  <div>
    <FlashNotifications ref="flash" />

    <div class="px-4 sm:px-6 py-5 max-w-5xl mx-auto">

      <!-- Title + search + export -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div class="min-w-0">
          <div class="flex items-center gap-2.5">
            <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">
              {{ I18N.website_content?.app_nav?.attendees || 'Attendees' }}
            </h2>
            <span v-if="!loading && !error && attendees.length > 0"
              class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 tabular-nums">
              {{ attendees.length }}
            </span>
          </div>
          <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1 leading-none truncate">
            People who booked {{ event.name }}.
          </p>
        </div>

        <div class="flex items-center gap-2.5 flex-shrink-0">
          <div class="relative flex-1 sm:w-64">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
            </svg>
            <input v-model="searchQuery" type="search"
              :placeholder="I18N.application?.attendees?.search_placeholder || 'Search attendees'"
              class="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"/>
          </div>

          <button @click="downloadCSV" :disabled="attendees.length === 0"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/>
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      <!-- Loading skeleton -->
      <template v-if="loading">
        <!-- Desktop skeleton -->
        <div class="hidden sm:block bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-pulse">
          <div class="px-6 py-3 bg-gray-50 dark:bg-gray-800/40 flex gap-6">
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-28"></div>
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-28"></div>
          </div>
          <div v-for="i in skeletonRows" :key="i" class="flex items-center gap-4 px-6 py-4 border-t border-gray-50 dark:border-gray-800">
            <div class="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
            <div class="flex-1 space-y-1.5">
              <div class="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/2"></div>
            </div>
            <div class="h-6 w-12 bg-gray-100 dark:bg-gray-800 rounded-full hidden md:block"></div>
            <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-24 hidden lg:block"></div>
            <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-24 hidden lg:block"></div>
            <div class="w-7 h-7 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
          </div>
        </div>
        <!-- Mobile skeleton -->
        <div class="sm:hidden space-y-3">
          <div v-for="i in skeletonRows" :key="i" class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 p-4 animate-pulse">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
              <div class="flex-1 space-y-1.5">
                <div class="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-3/4"></div>
              </div>
              <div class="h-6 w-10 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- Error -->
      <template v-else-if="error">
        <div class="flex flex-col items-center justify-center py-24 text-center">
          <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
            </svg>
          </div>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
          <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
            Try again
          </button>
        </div>
      </template>

      <!-- Empty: nobody has booked this event yet -->
      <template v-else-if="attendees.length === 0">
        <div class="flex flex-col items-center justify-center py-20 text-center px-4">
          <div class="w-24 h-24 rounded-3xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-6">
            <svg class="w-12 h-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"/>
            </svg>
          </div>
          <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">No one has booked {{ event.name }} yet</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
            Share the booking link and attendees will show up here as soon as they schedule.
          </p>
          <div class="flex items-center gap-2 w-full max-w-md">
            <input :value="publicUrl" readonly
              class="flex-1 px-3 py-2 text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg truncate focus:outline-none"/>
            <button @click="copyPublicUrl"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors flex-shrink-0">
              {{ copied ? 'Copied' : 'Copy link' }}
            </button>
          </div>
        </div>
      </template>

      <!-- Empty: search returned nothing -->
      <template v-else-if="filteredAttendees.length === 0">
        <div class="flex flex-col items-center justify-center py-24 text-center">
          <div class="w-20 h-20 rounded-3xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-5">
            <svg class="w-10 h-10 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-2">No attendees found</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">Try a different search term or clear the search.</p>
          <button @click="searchQuery = ''"
            class="mt-5 px-4 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
            Clear search
          </button>
        </div>
      </template>

      <!-- Loaded -->
      <template v-else>
        <!-- Desktop table -->
        <div class="hidden sm:block bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div class="grid grid-cols-[1fr_auto_auto_auto_auto] items-center px-6 py-3 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-700">
            <button @click="toggleSort('full_name')"
              class="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200 transition-colors text-left">
              {{ I18N.application?.attendees?.user || 'Attendee' }}
              <SortIcon column="full_name" :active-column="sortColumn" :direction="sortDirection"/>
            </button>
            <button @click="toggleSort('bookings_count')"
              class="hidden md:flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200 transition-colors w-28 justify-center">
              {{ I18N.application?.index?.bookings || 'Bookings' }}
              <SortIcon column="bookings_count" :active-column="sortColumn" :direction="sortDirection"/>
            </button>
            <button @click="toggleSort('first_appeared_at')"
              class="hidden lg:flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200 transition-colors w-36">
              {{ I18N.application?.attendees?.first_booking || 'First Booking' }}
              <SortIcon column="first_appeared_at" :active-column="sortColumn" :direction="sortDirection"/>
            </button>
            <button @click="toggleSort('last_appeared_at')"
              class="hidden lg:flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200 transition-colors w-36">
              {{ I18N.application?.attendees?.last_booking || 'Last Booking' }}
              <SortIcon column="last_appeared_at" :active-column="sortColumn" :direction="sortDirection"/>
            </button>
            <span class="w-8"></span>
          </div>

          <div v-for="attendee in filteredAttendees" :key="attendee.email"
            class="group grid grid-cols-[1fr_auto_auto_auto_auto] items-center px-6 py-3.5 border-t border-gray-50 dark:border-gray-800 hover:bg-gray-50/60 dark:hover:bg-gray-800/30 transition-colors"
            :class="{ 'bg-yellow-50/60 dark:bg-yellow-900/10 border-l-2 border-l-yellow-400 dark:border-l-yellow-500': highlightedEmail === attendee.email }">

            <div class="flex items-center gap-3 min-w-0">
              <AttendeeAvatar :attendee="attendee" size="sm"/>
              <div class="min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight">{{ attendee.full_name }}</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ attendee.email }}</p>
              </div>
            </div>

            <div class="hidden md:flex w-28 justify-center items-center gap-1.5">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 tabular-nums">
                {{ attendee.bookings_count }}
              </span>
              <span v-if="attendee.canceled_count > 0"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 tabular-nums"
                :title="`${attendee.canceled_count} canceled`">
                {{ attendee.canceled_count }}c
              </span>
            </div>

            <div class="hidden lg:block w-36">
              <p class="text-xs text-gray-500 dark:text-gray-400">{{ attendee.first_appeared }}</p>
            </div>

            <div class="hidden lg:block w-36">
              <p class="text-xs text-gray-500 dark:text-gray-400">{{ attendee.last_appeared }}</p>
            </div>

            <div class="w-8 flex justify-end">
              <a :href="bookingsPath(attendee)" data-turbo="false"
                class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 dark:text-gray-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                :title="`View all bookings by ${attendee.full_name}`">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <!-- Mobile cards -->
        <div class="sm:hidden space-y-3">
          <div v-for="attendee in filteredAttendees" :key="attendee.email"
            class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden"
            :class="{ 'ring-2 ring-yellow-300 dark:ring-yellow-500/60': highlightedEmail === attendee.email }">
            <div class="flex items-center gap-3 p-4">
              <AttendeeAvatar :attendee="attendee"/>
              <div class="flex-1 min-w-0">
                <p class="font-semibold text-sm text-gray-900 dark:text-white leading-tight truncate">{{ attendee.full_name }}</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ attendee.email }}</p>
                <p class="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-1">Last booked {{ attendee.last_appeared }}</p>
              </div>
              <span class="flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 tabular-nums">
                {{ attendee.bookings_count }}
              </span>
              <a :href="bookingsPath(attendee)" data-turbo="false"
                class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <p class="text-xs text-gray-400 dark:text-gray-500 mt-3 text-right">
          Showing {{ filteredAttendees.length }} of {{ attendees.length }} attendees
        </p>
      </template>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useConstants } from 'composables/useConstants.js'
import { useEventAttendeesStore } from 'stores/eventAttendees.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import AttendeeAvatar from 'controllers/vue_components/events/attendees/AttendeeAvatar'

// Inline sort indicator — mirrors the global attendees page.
const SortIcon = {
  props: ['column', 'activeColumn', 'direction'],
  template: `
    <svg class="w-3 h-3 flex-shrink-0 transition-colors" :class="column === activeColumn ? 'text-blue-600 dark:text-blue-400' : 'text-gray-300 dark:text-gray-600'" fill="currentColor" viewBox="0 0 320 512">
      <path v-if="column === activeColumn && direction === 'asc'" d="M137.4 41.4c12.5-12.5 32.8-12.5 45.3 0l128 128c9.2 9.2 11.9 22.9 6.9 34.9s-16.6 19.8-29.6 19.8H32c-12.9 0-24.6-7.8-29.6-19.8s-2.2-25.7 6.9-34.9l128-128z"/>
      <path v-else-if="column === activeColumn && direction === 'desc'" d="M182.6 470.6c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8H288c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128z"/>
      <path v-else d="M137.4 41.4c12.5-12.5 32.8-12.5 45.3 0l128 128c9.2 9.2 11.9 22.9 6.9 34.9s-16.6 19.8-29.6 19.8H32c-12.9 0-24.6-7.8-29.6-19.8s-2.2-25.7 6.9-34.9l128-128zm0 429.3l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8H288c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128c-12.5 12.5-32.8 12.5-45.3 0z"/>
    </svg>
  `
}

// The event is loaded once by EventLayout, which also renders the sub nav.
const props = defineProps({
  event: { type: Object, required: true },
})

const route = useRoute()
const { I18N, APPLICATION_URL } = useConstants()
const flash = ref(null)
const store = useEventAttendeesStore()

const eventId = route.params.id

const searchQuery      = ref('')
const sortColumn       = ref('bookings_count')
const sortDirection    = ref('desc')
const highlightedEmail = ref('')
const copied           = ref(false)

const skeletonRows = 6

const entry     = computed(() => store.stateFor(eventId))
const attendees = computed(() => entry.value.attendees)
const loading   = computed(() => entry.value.loading)
const error     = computed(() => entry.value.error)

const publicUrl = computed(() =>
  props.event.absolute_url ? `${APPLICATION_URL}${props.event.absolute_url}` : ''
)

// The attendee bookings page is global (all events), so it stays a plain link
// rather than a router-link — it is not a Vue Router route.
function bookingsPath(attendee) {
  return `/attendees/${attendee.ids.join('__')}/bookings`
}

function toggleSort(column) {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortColumn.value = column
    sortDirection.value = column === 'bookings_count' ? 'desc' : 'asc'
  }
}

const filteredAttendees = computed(() => {
  let list = [...attendees.value]

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase()
    list = list.filter((a) =>
      a.full_name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q)
    )
  }

  // Sorting uses the ISO timestamps the API returns, not the localised display
  // strings — no date re-parsing, and it stays correct in every locale.
  list.sort((a, b) => {
    const dir = sortDirection.value === 'asc' ? 1 : -1
    switch (sortColumn.value) {
      case 'full_name':
        return (a.full_name || '').localeCompare(b.full_name || '') * dir
      case 'bookings_count':
        return (a.bookings_count - b.bookings_count) * dir
      case 'first_appeared_at':
      case 'last_appeared_at':
        return (new Date(a[sortColumn.value] || 0) - new Date(b[sortColumn.value] || 0)) * dir
      default:
        return 0
    }
  })

  const hi = list.findIndex((a) => a.email === highlightedEmail.value)
  if (hi > 0) {
    const [item] = list.splice(hi, 1)
    list.unshift(item)
  }

  return list
})

function downloadCSV() {
  const headers = ['Full Name', 'Email', 'Bookings', 'Canceled', 'First Booking', 'Last Booking']
  const rows = attendees.value.map((a) => [
    a.full_name ?? '', a.email ?? '', a.bookings_count ?? 0, a.canceled_count ?? 0,
    a.first_appeared ?? '', a.last_appeared ?? '',
  ])
  const csv = [headers, ...rows]
    .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${(props.event.name || 'Event').replace(/[^\w\s-]/g, '')} Attendees.csv`
  link.click()
  URL.revokeObjectURL(url)
}

async function copyPublicUrl() {
  try {
    await navigator.clipboard.writeText(publicUrl.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch {
    flash.value?.show('Could not copy the link.', 'alert')
  }
}

async function retry() {
  store.invalidate(eventId)
  await store.fetchIfStale(eventId)
}

onMounted(async () => {
  highlightedEmail.value = route.query.attendee || ''
  await store.fetchIfStale(eventId)
})
</script>
